import { useEffect, useState } from 'react';
import BigNumber from 'bignumber.js';

import EthereumWallet from '#/features/ethereum-wallet/wallet';
import { logException } from '#/features/logging/logging';
import { BridgedToken } from '#/features/paraclear';
import { useWalletState } from '#/features/wallet/wallet-context';

import { fromQuantums } from '#/utils/quantums';

export interface EthereumErc20BalanceState {
  readonly status: 'idle' | 'loading' | 'success' | 'error';
  readonly value: BigNumber | null;
  readonly error: string;
}

export const useEthereumErc20Balance = (
  token: BridgedToken,
): EthereumErc20BalanceState => {
  const [walletState] = useWalletState();

  const [error, setError] = useState('');
  const [status, setStatus] =
    useState<EthereumErc20BalanceState['status']>('idle');
  const [value, setValue] = useState<EthereumErc20BalanceState['value']>(null);

  useEffect(() => {
    if (walletState.ethereumAddress == null) return;
    setStatus('loading');

    EthereumWallet.getErc20Balance(token.l1TokenAddress)
      .then((result) => {
        setStatus('success');
        setError('');
        setValue(fromQuantums(result, token.decimals));
      })
      .catch((err: Error) => {
        setStatus('error');
        const description = 'Error loading USDC balance';
        logException(new Error(description, { cause: err }));
        setError(`Error loading USDC balance: ${err.message}`);
        setValue(null);
      });
  }, [token.decimals, token.l1TokenAddress, walletState.ethereumAddress]);

  return { status, value, error };
};
